import { useEffect, useMemo, useRef, useState } from "react";

import { cleanupTranscription, transcribeAudio } from "@/lib/api";

export type DictationStatus = "idle" | "recording" | "transcribing" | "processing";

type UseDictationOptions = {
  onTranscript: (text: string) => void | Promise<void>;
  onError: (message: string) => void;
  maxDurationMs?: number;
};

type UseDictationResult = {
  status: DictationStatus;
  isSupported: boolean;
  toggleRecording: () => Promise<void>;
  stopRecording: () => void;
  elapsedMs: number;
  remainingMs: number;
  level: number;
  maxDurationMs: number;
};

const DEFAULT_MAX_DURATION_MS = 120_000;
const DEFAULT_TRANSCRIPTION_TIMEOUT_MS = 65_000;

const PREFERRED_MIME_TYPES = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4"];

type WindowWithWebkitAudioContext = Window & {
  webkitAudioContext?: typeof AudioContext;
};

function isMediaRecorderAvailable(): boolean {
  return typeof window !== "undefined" && typeof MediaRecorder !== "undefined";
}

function isGetUserMediaAvailable(): boolean {
  if (typeof navigator === "undefined") {
    return false;
  }
  return !!navigator.mediaDevices?.getUserMedia;
}

function pickSupportedMimeType(): string | null {
  if (!isMediaRecorderAvailable()) {
    return null;
  }

  if (typeof MediaRecorder.isTypeSupported !== "function") {
    return PREFERRED_MIME_TYPES[0] ?? null;
  }

  for (const mimeType of PREFERRED_MIME_TYPES) {
    if (MediaRecorder.isTypeSupported(mimeType)) {
      return mimeType;
    }
  }

  return null;
}

function extensionForMimeType(mimeType: string): string {
  if (mimeType.includes("mp4")) {
    return ".mp4";
  }
  if (mimeType.includes("mpeg")) {
    return ".mp3";
  }
  if (mimeType.includes("wav")) {
    return ".wav";
  }
  if (mimeType.includes("m4a")) {
    return ".m4a";
  }
  return ".webm";
}

function isPermissionDeniedError(error: unknown): boolean {
  if (!(error instanceof DOMException)) {
    return false;
  }

  return error.name === "NotAllowedError" || error.name === "SecurityError";
}

function getErrorMessage(error: unknown): string {
  if (error instanceof DOMException && error.name === "AbortError") {
    return "Transcription timed out";
  }

  return error instanceof Error ? error.message : "Failed to transcribe audio";
}

export function useDictation({
  onTranscript,
  onError,
  maxDurationMs = DEFAULT_MAX_DURATION_MS,
}: UseDictationOptions): UseDictationResult {
  const [status, setStatus] = useState<DictationStatus>("idle");
  const [elapsedMs, setElapsedMs] = useState(0);
  const [level, setLevel] = useState(0);

  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const mimeTypeRef = useRef<string>("audio/webm");
  const stopTimerRef = useRef<number | null>(null);
  const elapsedTimerRef = useRef<number | null>(null);
  const recordingStartedAtRef = useRef<number>(0);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const meterBufferRef = useRef<Uint8Array<ArrayBuffer> | null>(null);
  const levelRafRef = useRef<number | null>(null);
  const transcriptionAbortRef = useRef<AbortController | null>(null);
  const skipTranscriptionRef = useRef(false);
  const isMountedRef = useRef(true);

  const onTranscriptRef = useRef(onTranscript);
  const onErrorRef = useRef(onError);

  useEffect(() => {
    onTranscriptRef.current = onTranscript;
  }, [onTranscript]);

  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  const supportedMimeType = useMemo(() => pickSupportedMimeType(), []);
  const isSupported = useMemo(
    () => isMediaRecorderAvailable() && isGetUserMediaAvailable() && !!supportedMimeType,
    [supportedMimeType],
  );

  function clearStopTimer(): void {
    const stopTimer = stopTimerRef.current;
    if (stopTimer !== null) {
      window.clearTimeout(stopTimer);
      stopTimerRef.current = null;
    }
  }

  function stopStreamTracks(): void {
    const stream = streamRef.current;
    if (!stream) {
      return;
    }

    for (const track of stream.getTracks()) {
      track.stop();
    }

    streamRef.current = null;
  }

  function setStatusSafely(nextStatus: DictationStatus): void {
    if (isMountedRef.current) {
      setStatus(nextStatus);
    }
  }

  function setElapsedSafely(nextElapsedMs: number): void {
    if (isMountedRef.current) {
      setElapsedMs(nextElapsedMs);
    }
  }

  function setLevelSafely(nextLevel: number): void {
    if (isMountedRef.current) {
      setLevel(nextLevel);
    }
  }

  function clearElapsedTimer(): void {
    const elapsedTimer = elapsedTimerRef.current;
    if (elapsedTimer !== null) {
      window.clearInterval(elapsedTimer);
      elapsedTimerRef.current = null;
    }
  }

  function startElapsedTimer(): void {
    clearElapsedTimer();

    const startedAt = Date.now();
    recordingStartedAtRef.current = startedAt;
    setElapsedSafely(0);

    const update = () => {
      const elapsed = Math.min(Date.now() - startedAt, maxDurationMs);
      setElapsedSafely(elapsed);
    };

    update();
    elapsedTimerRef.current = window.setInterval(update, 200);
  }

  function stopAudioMeter(): void {
    const levelRaf = levelRafRef.current;
    if (levelRaf !== null) {
      window.cancelAnimationFrame(levelRaf);
      levelRafRef.current = null;
    }

    try {
      sourceRef.current?.disconnect();
      analyserRef.current?.disconnect();
    } catch {
      // Ignore node disconnect failures during teardown.
    }

    sourceRef.current = null;
    analyserRef.current = null;
    meterBufferRef.current = null;

    const audioContext = audioContextRef.current;
    if (audioContext && audioContext.state !== "closed") {
      void audioContext.close().catch(() => {});
    }
    audioContextRef.current = null;
    setLevelSafely(0);
  }

  function startAudioMeter(stream: MediaStream): void {
    stopAudioMeter();

    const AudioContextCtor =
      window.AudioContext || (window as WindowWithWebkitAudioContext).webkitAudioContext;
    if (!AudioContextCtor) {
      return;
    }

    try {
      const audioContext = new AudioContextCtor();
      const source = audioContext.createMediaStreamSource(stream);
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 256;
      analyser.smoothingTimeConstant = 0.82;
      source.connect(analyser);

      const buffer = new Uint8Array(analyser.fftSize);

      audioContextRef.current = audioContext;
      sourceRef.current = source;
      analyserRef.current = analyser;
      meterBufferRef.current = buffer;

      const updateLevel = () => {
        const activeAnalyser = analyserRef.current;
        const activeBuffer = meterBufferRef.current;
        if (!activeAnalyser || !activeBuffer) {
          return;
        }

        activeAnalyser.getByteTimeDomainData(activeBuffer);
        let sumSquares = 0;
        for (const value of activeBuffer) {
          const normalized = (value - 128) / 128;
          sumSquares += normalized * normalized;
        }

        const rms = Math.sqrt(sumSquares / activeBuffer.length);
        const normalizedLevel = Math.max(0, Math.min(1, rms * 2.8));
        setLevelSafely(normalizedLevel);
        levelRafRef.current = window.requestAnimationFrame(updateLevel);
      };

      updateLevel();
    } catch {
      stopAudioMeter();
    }
  }

  async function handleRecorderStop(): Promise<void> {
    clearStopTimer();
    clearElapsedTimer();
    stopStreamTracks();
    stopAudioMeter();
    recorderRef.current = null;

    if (skipTranscriptionRef.current) {
      chunksRef.current = [];
      setStatusSafely("idle");
      setElapsedSafely(0);
      return;
    }

    const mimeType = mimeTypeRef.current;
    const blob = new Blob(chunksRef.current, { type: mimeType });
    chunksRef.current = [];

    if (blob.size <= 0) {
      onErrorRef.current("No audio captured");
      setStatusSafely("idle");
      setElapsedSafely(0);
      return;
    }

    try {
      const extension = extensionForMimeType(mimeType);
      const file = new File([blob], `dictation-${Date.now()}${extension}`, {
        type: mimeType,
      });
      const abortController = new AbortController();
      transcriptionAbortRef.current = abortController;
      const timeoutId = window.setTimeout(() => {
        abortController.abort();
      }, DEFAULT_TRANSCRIPTION_TIMEOUT_MS);
      try {
        // Stage 1: audio → raw text
        const raw = await transcribeAudio(file, abortController.signal);

        // Stage 2: raw text → cleaned text
        setStatusSafely("processing");
        const cleaned = await cleanupTranscription(raw.text, abortController.signal);
        transcriptionAbortRef.current = null;
        await onTranscriptRef.current(cleaned.text);
      } finally {
        window.clearTimeout(timeoutId);
      }
    } catch (error) {
      transcriptionAbortRef.current = null;
      onErrorRef.current(getErrorMessage(error));
    } finally {
      setStatusSafely("idle");
      setElapsedSafely(0);
    }
  }

  function stopRecording(): void {
    const recorder = recorderRef.current;
    if (!recorder || recorder.state === "inactive") {
      return;
    }

    skipTranscriptionRef.current = false;
    setStatusSafely("transcribing");
    recorder.stop();
  }

  async function startRecording(): Promise<void> {
    if (!isSupported) {
      onErrorRef.current("Dictation is not supported in this browser");
      return;
    }

    if (status !== "idle" || recorderRef.current) {
      return;
    }

    clearStopTimer();
    chunksRef.current = [];
    skipTranscriptionRef.current = false;

    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (error) {
      if (isPermissionDeniedError(error)) {
        onErrorRef.current("Microphone access denied");
      } else {
        onErrorRef.current("Unable to start audio recording");
      }
      return;
    }

    const recorderOptions = supportedMimeType ? { mimeType: supportedMimeType } : undefined;

    let recorder: MediaRecorder;
    try {
      recorder = new MediaRecorder(stream, recorderOptions);
    } catch {
      for (const track of stream.getTracks()) {
        track.stop();
      }
      onErrorRef.current("Unable to initialize audio recorder");
      return;
    }

    streamRef.current = stream;
    recorderRef.current = recorder;
    mimeTypeRef.current = recorder.mimeType || supportedMimeType || "audio/webm";

    recorder.ondataavailable = (event: BlobEvent) => {
      if (event.data.size > 0) {
        chunksRef.current.push(event.data);
      }
    };

    recorder.addEventListener("error", () => {
      skipTranscriptionRef.current = true;
      clearStopTimer();
      clearElapsedTimer();
      stopStreamTracks();
      stopAudioMeter();
      recorderRef.current = null;
      chunksRef.current = [];
      onErrorRef.current("Recording failed");
      setStatusSafely("idle");
      setElapsedSafely(0);
    });

    recorder.onstop = () => {
      void handleRecorderStop();
    };

    startAudioMeter(stream);
    startElapsedTimer();
    recorder.start();
    setStatusSafely("recording");

    stopTimerRef.current = window.setTimeout(() => {
      stopRecording();
    }, maxDurationMs);
  }

  async function toggleRecording(): Promise<void> {
    if (status === "recording") {
      stopRecording();
      return;
    }

    if (status === "transcribing") {
      return;
    }

    await startRecording();
  }

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
      skipTranscriptionRef.current = true;
      clearStopTimer();
      clearElapsedTimer();

      const recorder = recorderRef.current;
      if (recorder && recorder.state !== "inactive") {
        recorder.stop();
      }

      transcriptionAbortRef.current?.abort();
      transcriptionAbortRef.current = null;
      stopStreamTracks();
      stopAudioMeter();
      recorderRef.current = null;
      chunksRef.current = [];
    };
  }, []);

  const remainingMs = Math.max(0, maxDurationMs - elapsedMs);

  return {
    status,
    isSupported,
    toggleRecording,
    stopRecording,
    elapsedMs,
    remainingMs,
    level,
    maxDurationMs,
  };
}
